import React, { useEffect, useMemo, useState, useCallback, Fragment } from 'react'
import { Box, CircularProgress, Grid, IconButton, Typography } from "@mui/material";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import InfiniteScroll from "react-infinite-scroll-component";
import { useDispatch } from "react-redux";
import { openSnackbar } from "../../redux/slices/common";
import { openCustomModalDialog } from "../../redux/slices/modalSlice";
import NoDataFound from "common/component/NoDataFound";
import { TableLoader } from "components/tables/AgGridAccordionChildScrollingTable";

const defaultPage = {
  page: 1,
  limit: 20
}

const brandKey = (row) => row?.brand?._id ?? row?.brand?.name ?? ''

const productNameKey = (row) => row?.productName ?? row?.name ?? ''

/** Title case for display: first letter of each word capitalized. */
const formatBrandHeading = (name) => {
  if (name == null || name === '') return '-'
  const s = String(name).trim()
  if (!s) return '-'
  return s
    .split(/\s+/)
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : word))
    .join(' ')
}

/**
 * Preserves box order as first seen in the API payload, then brand A–Z, then product name A–Z.
 */
const sortRowsForBoxBrandProduct = (rows) => {
  if (!rows?.length) return []
  const boxOrder = new Map()
  rows.forEach((row) => {
    const id = row?.box?._id
    if (id != null && !boxOrder.has(id)) boxOrder.set(id, boxOrder.size)
  })
  return [...rows].sort((a, b) => {
    const idA = a?.box?._id
    const idB = b?.box?._id
    const oa = boxOrder.get(idA) ?? 999
    const ob = boxOrder.get(idB) ?? 999
    if (oa !== ob) return oa - ob

    const bCmp = (brandKey(a) || '').localeCompare(brandKey(b) || '', undefined, { sensitivity: 'base' })
    if (bCmp !== 0) return bCmp

    return (productNameKey(a) || '').localeCompare(productNameKey(b) || '', undefined, { sensitivity: 'base' })
  })
}

const InfiniteScrollTableGrouping = ({
  id = "",
  columnDefs,
  refreshTable,
  getDataMethod = () => { },
  customFilters = {},
  onSelectionChange = () => { },
  showDeleteBoxButton = false,
  handleDeleteBox = () => { },
  tableHeight = 450,
  setSelectedBoxes = () => { },
}) => {
  const dispatch = useDispatch()

  const [paginationDetails, setPaginationDetails] = useState({ ...defaultPage });
  const [hasMore, setHasMore] = useState(true);
  const [childrenList, setChildrenList] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [sortConfig, setSortConfig] = useState({ sort: "", order: "" });

  const handleGetData = useCallback(async (filters = {}) => {
    setIsLoading(true)
    try {
      if (!getDataMethod) return;

      const request = { ...customFilters, ...paginationDetails, ...sortConfig, ...filters };

      const response = await dispatch(getDataMethod(request)).unwrap();
      const data = response?.data || response?.products

      if (data) {
        let myData = {};
        if (request.page === 1) {
          myData = { ...response, data }
        } else {
          myData = { ...response, data: [...(childrenList?.data || []), ...data] }
        }

        setChildrenList(myData);

        const hasMoreData = response?.total > (myData?.data?.length || 0);
        if (hasMoreData) {
          setPaginationDetails(prev => ({ ...prev, page: prev.page + 1 }));
          setHasMore(true);
        } else {
          setHasMore(false);
        }
      }
    } catch (error) {
      const errorMsg = error?.response?.data?.message || error?.message || error;
      dispatch(openSnackbar({ content: errorMsg, color: "error", icon: "warning", }));
    } finally {
      setIsLoading(false);
    }
  }, [customFilters, dispatch, getDataMethod, paginationDetails, sortConfig]);

  const handleSort = (field) => {
    let newDir = "asc";
    if (sortConfig.sort === field) {
      if (sortConfig.order === "asc") newDir = "desc";
      else if (sortConfig.order === "desc") newDir = "";
    }

    const newConfig = { sort: newDir ? field : "", order: newDir };
    setSortConfig(newConfig);
    setChildrenList({});
    setPaginationDetails({ ...defaultPage });
    handleGetData({ ...defaultPage, ...newConfig });
  };

  const memoizedColumnDefs = useMemo(() => columnDefs, [columnDefs]);

  const sortedDisplayData = useMemo(
    () => sortRowsForBoxBrandProduct(childrenList?.data || []),
    [childrenList?.data]
  );

  const handleSelectAll = (e, boxId) => {
    const { checked } = e.target;

    setSelectedRows(prev => {
      const updated = [...prev];
      const index = updated?.findIndex((item) => item.boxId === boxId);

      if (index >= 0) {
        updated[index] = { ...updated[index], boxId, isSelectedAll: checked, products: [] }
      } else {
        updated.push({ boxId, isSelectedAll: checked, products: [] });
      }

      onSelectionChange(updated);
      return updated;
    });
  };

  const handleRowSelection = (e, rowId, boxId) => {
    const { checked } = e.target;

    setSelectedRows(prev => {
      let updatedRows = [...prev];

      const index = updatedRows?.findIndex((item) => item.boxId === boxId);
      if (index >= 0) {
        let products = [];

        if (updatedRows[index]?.isSelectedAll) {
          const checkboxColumn = memoizedColumnDefs?.find(col => col.checkboxSelection);
          if (checkboxColumn && childrenList?.data) {
            products = childrenList.data
              .filter(row => row?.boxId === boxId)
              .map(row => (checkboxColumn.setRowId({ data: row })))
              .filter(id => id !== rowId);
          }
        } else if (checked) {
          products = [...(updatedRows[index].products || []), rowId];
        } else {
          products = updatedRows[index]?.products?.filter(item => item !== rowId)
        }

        updatedRows[index] = {
          ...updatedRows[index],
          boxId,
          isSelectedAll: false,
          products: products
        }
      } else {
        updatedRows.push({ boxId, isSelectedAll: false, products: [rowId] });
      }

      onSelectionChange(updatedRows);
      return updatedRows;
    });
  };

  useEffect(() => {
    setChildrenList({});
    setPaginationDetails({ ...defaultPage });
    handleGetData({ ...defaultPage })
  }, [refreshTable]);

  const scrollTargetId = id || "infiniteScrollTableGrouping";

  return (
    <>
      <Grid container alignItems='center' className="table-with-accordion">
        <div
          className="cus-ag-grid"
          style={{ height: tableHeight, width: "100%", overflowY: "auto" }}
          id={scrollTargetId}
        >
          {
            isLoading && paginationDetails?.page === 1
              ? <TableLoader />
              : <InfiniteScroll
                dataLength={childrenList?.data?.length || 0}
                next={handleGetData}
                hasMore={hasMore}
                loader={
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
                    <CircularProgress size={32} />
                  </div>
                }
                scrollableTarget={scrollTargetId}
              >
                {
                  (sortedDisplayData?.length || 0) > 0
                    ? sortedDisplayData.map((data, idx, arr) => {
                      const prev = idx > 0 ? arr[idx - 1] : null;
                      const boxId = data?.box?._id;
                      const prevBoxId = prev?.box?._id;
                      const isNewBox = boxId !== prevBoxId;

                      const bKey = brandKey(data);
                      const prevBKey = prev ? brandKey(prev) : null;
                      const isNewBrand = isNewBox || bKey !== prevBKey;

                      return (
                        <Fragment key={(data?._id || data?.id || idx)}>
                          {isNewBox && (
                            <>
                              <Box className="accordion-header cus-table-row" sx={{ width: "100%", background: "#f5f5f5" }}>
                                <Typography className="cus-table-cell" style={{ fontWeight: 600, fontSize: 14, width: "100%" }}>
                                  {data?.box?.name || "-"} {data?.box?.totalProducts ? `(${data?.box?.totalProducts})` : null}
                                </Typography>
                                {
                                  showDeleteBoxButton &&
                                  <Box display="flex" flexDirection="row" gap={1} className="field-actions">
                                    <IconButton
                                      size='small'
                                      variant='primary'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dispatch(openCustomModalDialog({
                                          modalData: { box: data?.box, setSelectedBoxes },
                                          modal: 'BULK_ASSIGN_QTY'
                                        }))
                                      }}
                                      style={{ fontSize: "12px", fontWeight: "600", padding: "6px 10px", boxShadow: "none", background: "#0163FF", color: "#ffffff" }}
                                    >
                                      Bulk Assign Qty
                                    </IconButton>
                                    <IconButton
                                      color='error'
                                      size='small'
                                      variant='primary'
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dispatch(openCustomModalDialog({
                                          modalData: { deleteHandler: () => handleDeleteBox(data) },
                                          modal: 'DELETE_MODAL'
                                        }))
                                      }}
                                      style={{ fontSize: "12px", fontWeight: "600", padding: "6px 10px", boxShadow: "none", color: "#2E2E3A" }}
                                    >
                                      Remove Box
                                    </IconButton>
                                  </Box>
                                }
                              </Box>
                            </>
                          )}

                          <div className="table-listing">
                            {isNewBox && (
                              <>
                                <Box className="cus-table-header" style={{ borderRadius: 0 }}>
                                  {
                                    memoizedColumnDefs?.map((column, colIdx) => {
                                      let { style = {}, minWidth, flex, headerName, field, selectedAll, sortable } = column || {};

                                      if (minWidth) {
                                        style.minWidth = minWidth;
                                      }
                                      if (flex) {
                                        style.flex = flex;
                                      }

                                      const isAllSelected = selectedRows?.find((row) => row?.boxId === data?.boxId)?.isSelectedAll;

                                      return (
                                        <Fragment key={headerName || field || colIdx}>
                                          {selectedAll && <Box className="form-check px-1 d-flex align-item-center gap-2 text-14">
                                            <input
                                              className="form-check-input cur-pointer"
                                              type="checkbox"
                                              id={`flexCheckIndeterminate`}
                                              checked={isAllSelected}
                                              onChange={e => handleSelectAll(e, data?.boxId)}
                                            />
                                          </Box>}
                                          <Typography
                                            className="cus-table-cell"
                                            style={{ ...style, cursor: sortable ? "pointer" : "default", display: "flex", alignItems: "center", gap: "4px" }}
                                            onClick={() => sortable && handleSort(field)}
                                          >
                                            {headerName}
                                            {sortable && sortConfig.sort === field && (
                                              sortConfig.order === 'asc' ? <ArrowUpwardIcon sx={{ fontSize: '14px' }} /> : <ArrowDownwardIcon sx={{ fontSize: '14px' }} />
                                            )}
                                          </Typography>
                                        </Fragment>
                                      )
                                    })
                                  }
                                </Box>
                              </>
                            )}

                            {isNewBrand && (
                              <Box
                                className="accordion-header cus-table-row"
                                sx={{
                                  width: "100%",
                                  background: "#ebebeb",
                                  borderTop: isNewBox ? undefined : '1px solid #e0e0e0',
                                  py: '4px',
                                  px: '20px',
                                  minHeight: 0,
                                  alignItems: 'center',
                                  '& .MuiTypography-root': {
                                    margin: 0,
                                    padding: 0,
                                    lineHeight: 1.3,
                                  },
                                }}
                              >
                                <Typography
                                  component="div"
                                  className="cus-table-cell"
                                  sx={{ fontWeight: 600, fontSize: 12, width: "100%" }}
                                >
                                  {formatBrandHeading(data?.brand?.name)}
                                </Typography>
                              </Box>
                            )}

                            <Box className="accordion-header cus-table-row" sx={{ width: "100%" }}>
                              {
                                memoizedColumnDefs?.map((column) => {
                                  let { style = {}, minWidth, flex, headerName, field, cellRenderer, valueGetter, checkboxSelection, setRowId } = column || {};
                                  const rowId = setRowId ? setRowId({ data: data }) : "";

                                  const cellValue = cellRenderer
                                    ? cellRenderer({ data: data, colDef: column })
                                    : valueGetter
                                      ? valueGetter({ data: data })
                                      : data[field];

                                  if (minWidth) {
                                    style.minWidth = minWidth;
                                  }
                                  if (flex) {
                                    style.flex = flex;
                                  }

                                  return (
                                    <Fragment key={`${field || headerName}-${data?._id || data?.id || idx}`}>
                                      {(() => {
                                        if (checkboxSelection && rowId) {
                                          const boxSel = selectedRows?.find(item => item.boxId === data?.boxId) || {}
                                          const isRowSelected = boxSel.isSelectedAll || boxSel?.products?.some(id => id === rowId);

                                          return (<Box className="form-check px-1 d-flex align-item-center gap-2 text-14">
                                            <input
                                              className="form-check-input cur-pointer"
                                              type="checkbox"
                                              id={`flexCheckIndeterminate`}
                                              name={rowId}
                                              checked={isRowSelected}
                                              onChange={e => handleRowSelection(e, rowId, data?.boxId, data)}
                                            />
                                          </Box>)
                                        }
                                      })()}

                                      <Typography
                                        className="cus-table-cell" style={{ ...style }}
                                      >
                                        {cellValue}
                                      </Typography>
                                    </Fragment>
                                  )
                                })
                              }
                            </Box>
                          </div>
                        </Fragment>
                      )
                    })
                    : childrenList?.data?.length === 0
                      ? <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "360px", textAlign: "center" }}>
                        <NoDataFound />
                      </div>
                      : null
                }
              </InfiniteScroll>
          }
        </div>
      </Grid>
    </>
  )
}

export default InfiniteScrollTableGrouping
